/**
 * CCDB BFF API — Holidays
 * Backend for Frontend API for CCDB collection app.
 *
 * NOTE: This class is generated following OpenAPI Generator patterns.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {Holiday} from '../models/Holiday';
import type {HolidaySearchResponse} from '../models/HolidaySearchResponse';
import type {CcdbError} from '../models/CcdbError';

// ============================================
// Search Holidays
// ============================================

export interface SearchHolidays$Request {
    region: string;
    year?: number;
    page?: number;
    size?: number;
}

export interface SearchHolidays$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const searchHolidaysRequest = createAction<PayloadData<void, SearchHolidays$Request>>('CCDB_BFF__SEARCH_HOLIDAYS__REQUEST');
export const searchHolidaysSuccess = createAction<PayloadData<HolidaySearchResponse, SearchHolidays$Request>>('CCDB_BFF__SEARCH_HOLIDAYS__SUCCESS');
export const searchHolidaysFailure = createAction<PayloadData<CcdbError, SearchHolidays$Request, true>>('CCDB_BFF__SEARCH_HOLIDAYS__FAILURE');

export const searchHolidaysActions = [
    searchHolidaysRequest,
    searchHolidaysSuccess,
    searchHolidaysFailure,
] as const;

/**
 * Search for holidays by region and year
 * @param {string} meta.region - Region code (UK, US, etc.)
 * @param {number} meta.year - Year to search for
 */
export const searchHolidays = (meta: SearchHolidays$Request, config?: SearchHolidays$Config) => {
    const queryParams = new URLSearchParams();
    queryParams.set('region', meta.region);
    if (meta.year != null && meta.year > 0) {
        queryParams.set('year', String(meta.year));
    }
    queryParams.set('page', String(meta.page ?? 0));
    queryParams.set('size', String(meta.size ?? 20));

    return {
        CALL_API: {
            types: searchHolidaysActions,
            method: ApiMethods.GET,
            endpoint: `/api/holidays/search?${queryParams.toString()}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            },
        },
        ...meta,
    };
};

// ============================================
// Add Holiday
// ============================================

export interface AddHoliday$Request {
    holidayName: string;
    holidayDate: string;
    region: string;
    isActive?: boolean;
}

export interface AddHoliday$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const addHolidayRequest = createAction<PayloadData<void, AddHoliday$Request>>('CCDB_BFF__ADD_HOLIDAY__REQUEST');
export const addHolidaySuccess = createAction<PayloadData<Holiday, AddHoliday$Request>>('CCDB_BFF__ADD_HOLIDAY__SUCCESS');
export const addHolidayFailure = createAction<PayloadData<CcdbError, AddHoliday$Request, true>>('CCDB_BFF__ADD_HOLIDAY__FAILURE');

export const addHolidayActions = [
    addHolidayRequest,
    addHolidaySuccess,
    addHolidayFailure,
] as const;

/**
 * Add a new holiday
 * @param {string} meta.holidayDate - Date of the holiday (MM/DD/YYYY)
 * @param {string} meta.holidayName - Name of the holiday
 * @param {string} meta.region - Region code
 */
export const addHoliday = (meta: AddHoliday$Request, config?: AddHoliday$Config) => {
    return {
        CALL_API: {
            types: addHolidayActions,
            method: ApiMethods.POST,
            endpoint: '/api/holidays',
            body: JSON.stringify({
                holidayName: meta.holidayName,
                holidayDate: meta.holidayDate,
                region: meta.region,
                isActive: meta.isActive ?? true,
            }),
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        },
        ...meta,
    };
};

// ============================================
// Update Holiday
// ============================================

export interface UpdateHoliday$Request {
    id: number;
    holidayName: string;
    holidayDate: string;
    region: string;
    isActive?: boolean;
}

export interface UpdateHoliday$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const updateHolidayRequest = createAction<PayloadData<void, UpdateHoliday$Request>>('CCDB_BFF__UPDATE_HOLIDAY__REQUEST');
export const updateHolidaySuccess = createAction<PayloadData<Holiday, UpdateHoliday$Request>>('CCDB_BFF__UPDATE_HOLIDAY__SUCCESS');
export const updateHolidayFailure = createAction<PayloadData<CcdbError, UpdateHoliday$Request, true>>('CCDB_BFF__UPDATE_HOLIDAY__FAILURE');

export const updateHolidayActions = [
    updateHolidayRequest,
    updateHolidaySuccess,
    updateHolidayFailure,
] as const;

/**
 * Update an existing holiday
 * @param {number} meta.id - The holiday ID
 * @param {string} meta.holidayName - Name of the holiday
 * @param {string} meta.holidayDate - Date of the holiday
 * @param {string} meta.region - Region code
 */
export const updateHoliday = (meta: UpdateHoliday$Request, config?: UpdateHoliday$Config) => {
    return {
        CALL_API: {
            types: updateHolidayActions,
            method: ApiMethods.PUT,
            endpoint: `/api/holidays/${encodeURIComponent(String(meta.id))}`,
            body: JSON.stringify({
                holidayName: meta.holidayName,
                holidayDate: meta.holidayDate,
                region: meta.region,
                isActive: meta.isActive ?? true,
            }),
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        },
        ...meta,
    };
};

// ============================================
// Delete Holiday
// ============================================

export interface DeleteHoliday$Request {
    id: number;
}

export interface DeleteHoliday$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const deleteHolidayRequest = createAction<PayloadData<void, DeleteHoliday$Request>>('CCDB_BFF__DELETE_HOLIDAY__REQUEST');
export const deleteHolidaySuccess = createAction<PayloadData<void, DeleteHoliday$Request>>('CCDB_BFF__DELETE_HOLIDAY__SUCCESS');
export const deleteHolidayFailure = createAction<PayloadData<CcdbError, DeleteHoliday$Request, true>>('CCDB_BFF__DELETE_HOLIDAY__FAILURE');

export const deleteHolidayActions = [
    deleteHolidayRequest,
    deleteHolidaySuccess,
    deleteHolidayFailure,
] as const;

/**
 * Delete a holiday by ID
 * @param {number} meta.id - The holiday ID to delete
 */
export const deleteHoliday = (meta: DeleteHoliday$Request, config?: DeleteHoliday$Config) => {
    return {
        CALL_API: {
            types: deleteHolidayActions,
            method: ApiMethods.DELETE,
            endpoint: `/api/holidays/${encodeURIComponent(String(meta.id))}`,

            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            },
        },
        ...meta,
    };
};
